import React, { useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import {
  Visibility,
  Edit,
  Delete,
  HelpOutline,
  PlayArrow,
  Pause,
} from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';

import { MainLayout } from '../components/layout/MainLayout';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { llmTemplatesAPI } from '../services/api';
import { useToast } from '../components/common/ToastProvider';
import { LLMTemplate } from '../services/interfaces';

const VOICES = [
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Clear', 'Confident', 'Knowledgeable', 'Energetic'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-asteria-en',
    name: 'asteria',
  },
  {
    accent: 'British',
    age: ['Mature'],
    category: 'Aura 1',
    characteristics: ['Calm', 'Smooth', 'Professional'],
    gender: 'Feminine',
    language: 'en-gb',
    model: 'aura-athena-en',
    name: 'athena',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Smooth', 'Warm', 'Professional'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-hera-en',
    name: 'hera',
  },
  {
    accent: 'American',
    age: ['Young', 'Adult'],
    category: 'Aura 1',
    characteristics: ['Friendly', 'Natural', 'Engaging'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-luna-en',
    name: 'luna',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    gender: 'Feminine',
    characteristics: ['Clear', 'Professional', 'Engaging'],
    language: 'en-us',
    model: 'aura-stella-en',
    name: 'stella',
  },
  {
    accent: 'Irish',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Warm', 'Friendly', 'Natural'],
    gender: 'Masculine',
    language: 'en-ie',
    model: 'aura-angus-en',
    name: 'angus',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Natural', 'Smooth', 'Clear', 'Comfortable'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-arcas-en',
    name: 'arcas',
  },
  {
    accent: 'British',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Professional', 'Clear', 'Confident'],
    gender: 'Masculine',
    language: 'en-gb',
    model: 'aura-helios-en',
    name: 'helios',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Approachable', 'Comfortable', 'Calm', 'Polite'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-orion-en',
    name: 'orion',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Professional', 'Clear', 'Confident', 'Trustworthy'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-orpheus-en',
    name: 'orpheus',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Confident', 'Professional', 'Clear'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-perseus-en',
    name: 'perseus',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 1',
    characteristics: ['Deep', 'Trustworthy', 'Smooth'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-zeus-en',
    name: 'zeus',
  },
  {
    accent: 'Filipino',
    age: ['Young', 'Adult'],
    category: 'Aura 2',
    characteristics: ['Engaging', 'Natural', 'Cheerful'],
    gender: 'Feminine',
    language: 'en-ph',
    model: 'aura-2-amalthea-en',
    name: 'amalthea',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Casual', 'Expressive', 'Comfortable'],
    featured: true,
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-andromeda-en',
    name: 'andromeda',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Clear', 'Confident', 'Knowledgeable', 'Energetic'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-asteria-en',
    name: 'asteria',
  },
  {
    accent: 'American',
    age: ['Mature'],
    category: 'Aura 2',
    characteristics: ['Calm', 'Smooth', 'Professional'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-athena-en',
    name: 'athena',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Cheerful', 'Expressive', 'Energetic'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-aurora-en',
    name: 'aurora',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Clear', 'Energetic', 'Professional', 'Smooth'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-callista-en',
    name: 'callista',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Smooth', 'Melodic', 'Caring'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-cora-en',
    name: 'cora',
  },
  {
    accent: 'American',
    age: ['Young', 'Adult'],
    category: 'Aura 2',
    characteristics: ['Approachable', 'Warm', 'Polite'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-cordelia-en',
    name: 'cordelia',
  },
  {
    accent: 'American',
    age: ['Young', 'Adult'],
    category: 'Aura 2',
    characteristics: ['Casual', 'Friendly', 'Cheerful', 'Breathy'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-delia-en',
    name: 'delia',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Professional', 'Engaging', 'Knowledgeable'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-electra-en',
    name: 'electra',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Empathetic', 'Clear', 'Calm', 'Confident'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-harmonia-en',
    name: 'harmonia',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Caring', 'Natural', 'Positive', 'Friendly', 'Raspy'],
    featured: true,
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-helena-en',
    name: 'helena',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Smooth', 'Warm', 'Professional'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-hera-en',
    name: 'hera',
  },
  {
    accent: 'American',
    age: ['Young', 'Adult'],
    category: 'Aura 2',
    characteristics: ['Cheerful', 'Positive', 'Approachable'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-iris-en',
    name: 'iris',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Southern', 'Smooth', 'Trustworthy'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-janus-en',
    name: 'janus',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Natural', 'Engaging', 'Melodic', 'Breathy'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-juno-en',
    name: 'juno',
  },
  {
    accent: 'American',
    age: ['Young', 'Adult'],
    category: 'Aura 2',
    characteristics: ['Friendly', 'Natural', 'Engaging'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-luna-en',
    name: 'luna',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Positive', 'Friendly', 'Natural'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-minerva-en',
    name: 'minerva',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Expressive', 'Enthusiastic', 'Cheerful'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-ophelia-en',
    name: 'ophelia',
  },
  {
    accent: 'British',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Smooth', 'Calm', 'Melodic', 'Breathy'],
    gender: 'Feminine',
    language: 'en-gb',
    model: 'aura-2-pandora-en',
    name: 'pandora',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Energetic', 'Warm', 'Casual'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-phoebe-en',
    name: 'phoebe',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Expressive', 'Engaging', 'Energetic'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-selene-en',
    name: 'selene',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Clear', 'Confident', 'Energetic', 'Enthusiastic'],
    featured: true,
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-thalia-en',
    name: 'thalia',
  },
  {
    accent: 'Australian',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Expressive', 'Polite', 'Sincere'],
    gender: 'Feminine',
    language: 'en-au',
    model: 'aura-2-theia-en',
    name: 'theia',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Natural', 'Expressive', 'Patient', 'Empathetic'],
    gender: 'Feminine',
    language: 'en-us',
    model: 'aura-2-vesta-en',
    name: 'vesta',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Confident', 'Comfortable', 'Casual'],
    featured: true,
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-apollo-en',
    name: 'apollo',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Natural', 'Smooth', 'Clear', 'Comfortable'],
    featured: true,
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-arcas-en',
    name: 'arcas',
  },
  {
    accent: 'American',
    age: ['Mature'],
    category: 'Aura 2',
    characteristics: ['Enthusiastic', 'Confident', 'Approachable', 'Friendly'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-atlas-en',
    name: 'atlas',
  },
  {
    accent: 'British',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Warm', 'Approachable', 'Trustworthy', 'Baritone'],
    gender: 'Masculine',
    language: 'en-gb',
    model: 'aura-2-draco-en',
    name: 'draco',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Expressive', 'Engaging', 'Professional'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-hermes-en',
    name: 'hermes',
  },
  {
    accent: 'Australian',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Caring', 'Warm', 'Empathetic'],
    gender: 'Masculine',
    language: 'en-au',
    model: 'aura-2-hyperion-en',
    name: 'hyperion',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Expressive', 'Knowledgeable', 'Baritone'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-jupiter-en',
    name: 'jupiter',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Smooth', 'Patient', 'Trustworthy', 'Baritone'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-mars-en',
    name: 'mars',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Professional', 'Patient', 'Polite'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-neptune-en',
    name: 'neptune',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Calm', 'Smooth', 'Comfortable', 'Professional'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-odysseus-en',
    name: 'odysseus',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Approachable', 'Comfortable', 'Calm', 'Polite'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-orion-en',
    name: 'orion',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Professional', 'Clear', 'Confident', 'Trustworthy'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-orpheus-en',
    name: 'orpheus',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Smooth', 'Calm', 'Empathetic', 'Baritone'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-pluto-en',
    name: 'pluto',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Knowledgeable', 'Confident', 'Baritone'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-saturn-en',
    name: 'saturn',
  },
  {
    accent: 'American',
    age: ['Adult'],
    category: 'Aura 2',
    characteristics: ['Deep', 'Trustworthy', 'Smooth'],
    gender: 'Masculine',
    language: 'en-us',
    model: 'aura-2-zeus-en',
    name: 'zeus',
  },
];

const VOICE_FILES: Record<string, string> = {
  'aura-angus-en': require('../voices/aura-1/aura-angus-en.wav'),
  'aura-arcas-en': require('../voices/aura-1/aura-arcas-en.mp3'),
  'aura-asteria-en': require('../voices/aura-1/aura-asteria-en.wav'),
  'aura-athena-en': require('../voices/aura-1/aura-athena-en.wav'),
  'aura-helios-en': require('../voices/aura-1/aura-helios-en.wav'),
  'aura-hera-en': require('../voices/aura-1/aura-hera-en.wav'),
  'aura-luna-en': require('../voices/aura-1/aura-luna-en.wav'),
  'aura-orion-en': require('../voices/aura-1/aura-orion-en.mp3'),
  'aura-orpheus-en': require('../voices/aura-1/aura-orpheus-en.wav'),
  'aura-perseus-en': require('../voices/aura-1/aura-perseus-en.wav'),
  'aura-stella-en': require('../voices/aura-1/aura-stella-en.wav'),
  'aura-zeus-en': require('../voices/aura-1/aura-zeus-en.wav'),
  'aura-2-amalthea-en': require('../voices/aura-2/aura-2-amalthea-en.wav'),
  'aura-2-andromeda-en': require('../voices/aura-2/aura-2-andromeda-en.wav'),
  'aura-2-asteria-en': require('../voices/aura-2/aura-2-asteria-en.wav'),
  'aura-2-athena-en': require('../voices/aura-2/aura-2-athena-en.wav'),
  'aura-2-aurora-en': require('../voices/aura-2/aura-2-aurora-en.wav'),
  'aura-2-callista-en': require('../voices/aura-2/aura-2-callista-en.wav'),
  'aura-2-cora-en': require('../voices/aura-2/aura-2-cora-en.wav'),
  'aura-2-cordelia-en': require('../voices/aura-2/aura-2-cordelia-en.wav'),
  'aura-2-delia-en': require('../voices/aura-2/aura-2-delia-en.wav'),
  'aura-2-electra-en': require('../voices/aura-2/aura-2-electra-en.wav'),
  'aura-2-harmonia-en': require('../voices/aura-2/aura-2-harmonia-en.wav'),
  'aura-2-helena-en': require('../voices/aura-2/aura-2-helena-en.wav'),
  'aura-2-hera-en': require('../voices/aura-2/aura-2-hera-en.wav'),
  'aura-2-iris-en': require('../voices/aura-2/aura-2-iris-en.wav'),
  'aura-2-janus-en': require('../voices/aura-2/aura-2-janus-en.wav'),
  'aura-2-juno-en': require('../voices/aura-2/aura-2-juno-en.wav'),
  'aura-2-luna-en': require('../voices/aura-2/aura-2-luna-en.wav'),
  'aura-2-minerva-en': require('../voices/aura-2/aura-2-minerva-en.wav'),
  'aura-2-ophelia-en': require('../voices/aura-2/aura-2-ophelia-en.wav'),
  'aura-2-pandora-en': require('../voices/aura-2/aura-2-pandora-en.wav'),
  'aura-2-phoebe-en': require('../voices/aura-2/aura-2-phoebe-en.wav'),
  'aura-2-selene-en': require('../voices/aura-2/aura-2-selene-en.wav'),
  'aura-2-thalia-en': require('../voices/aura-2/aura-2-thalia-en.wav'),
  'aura-2-theia-en': require('../voices/aura-2/aura-2-theia-en.wav'),
  'aura-2-vesta-en': require('../voices/aura-2/aura-2-vesta-en.wav'),
  'aura-2-apollo-en': require('../voices/aura-2/aura-2-apollo-en.wav'),
  'aura-2-arcas-en': require('../voices/aura-2/aura-2-arcas-en.wav'),
  'aura-2-atlas-en': require('../voices/aura-2/aura-2-atlas-en.wav'),
  'aura-2-draco-en': require('../voices/aura-2/aura-2-draco-en.wav'),
  'aura-2-hermes-en': require('../voices/aura-2/aura-2-hermes-en.wav'),
  'aura-2-hyperion-en': require('../voices/aura-2/aura-2-hyperion-en.wav'),
  'aura-2-jupiter-en': require('../voices/aura-2/aura-2-jupiter-en.wav'),
  'aura-2-mars-en': require('../voices/aura-2/aura-2-mars-en.wav'),
  'aura-2-neptune-en': require('../voices/aura-2/aura-2-neptune-en.wav'),
  'aura-2-odysseus-en': require('../voices/aura-2/aura-2-odysseus-en.wav'),
  'aura-2-orion-en': require('../voices/aura-2/aura-2-orion-en.wav'),
  'aura-2-orpheus-en': require('../voices/aura-2/aura-2-orpheus-en.wav'),
  'aura-2-pluto-en': require('../voices/aura-2/aura-2-pluto-en.wav'),
  'aura-2-saturn-en': require('../voices/aura-2/aura-2-saturn-en.wav'),
  'aura-2-zeus-en': require('../voices/aura-2/aura-2-zeus-en.wav'),
};

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const getChangedFields = (
  original: LLMTemplate | null,
  edited: LLMTemplate | null
): { field: string; oldValue: string; newValue: string }[] => {
  if (!original || !edited) return [];
  const changes: { field: string; oldValue: string; newValue: string }[] = [];
  const fieldsToCompare: (keyof LLMTemplate)[] = [
    'name',
    'isDefault',
    'description',
    'temperature',
    'systemPrompt',
  ];

  fieldsToCompare.forEach((field) => {
    const oldVal = original[field];
    const newVal = edited[field];
    if (oldVal !== newVal) {
      changes.push({
        field: String(field),
        oldValue: oldVal === undefined ? '(empty)' : String(oldVal),
        newValue: newVal === undefined ? '(empty)' : String(newVal),
      });
    }
  });

  return changes;
};

// TODO (dev) разделить на компоненты
export const SettingsPage: React.FC = () => {
  const { showToast } = useToast();

  const getInitialPromptQuery = useQuery(
    ['llmTemplatesAPI.getInitialPrompt'],
    () => llmTemplatesAPI.getInitialPrompt(),
    {
      enabled: true,
    }
  );

  const getTemplatesQuery = useQuery(
    ['llmTemplatesAPI.getTemplates'],
    () => llmTemplatesAPI.getTemplates(),
    {
      enabled: true,
    }
  );

  const updateTemplateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<LLMTemplate> }) =>
      llmTemplatesAPI.updateTemplate(id, data),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to update template', 'error');
      },
      onSuccess: () => {
        showToast('Template updated successfully', 'success');
        getTemplatesQuery.refetch();
        setConfirmUpdateOpen(false);
        setEditModalOpen(false);
        setEditTemplate(null);
        setOriginalTemplate(null);
        setIsCreateMode(false);
      },
    }
  );

  const createTemplateMutation = useMutation(
    (data: Partial<LLMTemplate>) => llmTemplatesAPI.createTemplate(data),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to create template', 'error');
      },
      onSuccess: () => {
        showToast('Template created successfully', 'success');
        getTemplatesQuery.refetch();
        setEditModalOpen(false);
        setEditTemplate(null);
        setOriginalTemplate(null);
        setIsCreateMode(false);
      },
    }
  );

  const deleteTemplateMutation = useMutation(
    (id: string) => llmTemplatesAPI.deleteTemplate(id),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to delete template', 'error');
      },
      onSuccess: () => {
        showToast('Template deleted successfully', 'success');
        getTemplatesQuery.refetch();
        setConfirmDeleteOpen(false);
        setSelectedTemplateId(null);
      },
    }
  );

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewTemplate, setViewTemplate] = useState<LLMTemplate | null>(null);

  // Edit/Create modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<LLMTemplate | null>(null);
  const [originalTemplate, setOriginalTemplate] = useState<LLMTemplate | null>(
    null
  );
  const [isCreateMode, setIsCreateMode] = useState(false);

  // Confirm update modal state
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);

  // Delete modal state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const [playingModel, setPlayingModel] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleOpenView = (template: LLMTemplate) => {
    setViewTemplate(template);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setViewTemplate(null);
  };

  const handleOpenCreate = () => {
    setIsCreateMode(true);
    setOriginalTemplate(null);
    setEditTemplate({
      id: '',
      name: '',
      createdAt: '',
      isDefault: false,
      description: '',
      systemPrompt: '',
      temperature: undefined,
    } as LLMTemplate);
    setEditModalOpen(true);
  };

  const handleOpenEdit = (template: LLMTemplate) => {
    setIsCreateMode(false);
    setOriginalTemplate(template);
    setEditTemplate({ ...template });
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditTemplate(null);
    setOriginalTemplate(null);
    setIsCreateMode(false);
  };

  const handleEditChange = (field: keyof LLMTemplate, value: unknown) => {
    if (editTemplate) {
      setEditTemplate({
        ...editTemplate,
        [field]: value,
      });
    }
  };

  const handleSaveClick = () => {
    if (isCreateMode) {
      if (editTemplate) {
        createTemplateMutation.mutate({
          name: editTemplate.name,
          isDefault: editTemplate.isDefault,
          description: editTemplate.description || undefined,
          systemPrompt: editTemplate.systemPrompt,
          temperature:
            editTemplate.temperature !== undefined &&
            editTemplate.temperature !== null
              ? parseFloat(String(editTemplate.temperature))
              : undefined,
        });
      }
    } else {
      setConfirmUpdateOpen(true);
    }
  };

  const handleConfirmUpdate = () => {
    if (editTemplate && originalTemplate) {
      updateTemplateMutation.mutate({
        id: editTemplate.id,
        data: {
          name: editTemplate.name,
          isDefault: editTemplate.isDefault,
          description: editTemplate.description || undefined,
          systemPrompt: editTemplate.systemPrompt,
          temperature:
            editTemplate.temperature !== undefined &&
            editTemplate.temperature !== null
              ? parseFloat(String(editTemplate.temperature))
              : undefined,
        },
      });
    }
  };

  const handleDeleteClick = (id: string) => {
    setSelectedTemplateId(id);
    setConfirmDeleteOpen(true);
  };

  const handleToggleVoice = (model: string) => {
    const src = VOICE_FILES[model];
    if (!src) return;

    if (playingModel === model) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingModel(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    const audio = new Audio(src);
    audioRef.current = audio;
    setPlayingModel(model);

    audio.play().catch((err) => {
      console.error('Failed to play audio', err);
      setPlayingModel(null);
      audioRef.current = null;
    });

    audio.onended = () => {
      setPlayingModel(null);
      audioRef.current = null;
    };
  };

  const handleConfirmDelete = () => {
    if (selectedTemplateId) {
      deleteTemplateMutation.mutate(selectedTemplateId);
    }
  };

  const isFormValid = useMemo(() => {
    if (!editTemplate) return false;
    const temp = editTemplate.temperature;
    return (
      !!editTemplate.name &&
      !!editTemplate.systemPrompt &&
      temp !== undefined &&
      temp !== null &&
      temp >= 0 &&
      temp <= 2
    );
  }, [editTemplate]);

  const renderInitialPromptContent = () => {
    if (getInitialPromptQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!getInitialPromptQuery.data) {
      return (
        <Typography color='text.secondary'>
          No initial prompt data available.
        </Typography>
      );
    }

    return (
      <Paper
        variant='outlined'
        sx={{
          p: 2,
          backgroundColor: 'grey.50',
          fontFamily: 'monospace',
        }}
      >
        {Object.entries(
          getInitialPromptQuery.data as Record<string, string>
        ).map(([key, value]) => (
          <Box
            key={key}
            sx={{ mb: 2 }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
              {key}:
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                pl: 1,
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  const renderTemplatesContent = () => {
    if (getTemplatesQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!getTemplatesQuery.data || getTemplatesQuery.data.length === 0) {
      return (
        <Typography color='text.secondary'>No templates available.</Typography>
      );
    }

    return (
      <TableContainer
        component={Paper}
        variant='outlined'
      >
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell>
                <Typography variant='subtitle2'>Name</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2'>Created At</Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography variant='subtitle2'>Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getTemplatesQuery.data.map((template) => {
              const canDelete =
                !template.isDefault &&
                (!template.campaigns || template.campaigns.length === 0);
              const deleteTooltip = template.isDefault
                ? 'Default template cannot be deleted'
                : template.campaigns && template.campaigns.length > 0
                ? 'Template is used in campaigns'
                : '';

              return (
                <TableRow
                  key={template.id}
                  hover
                >
                  <TableCell>{template.name}</TableCell>
                  <TableCell>{formatDate(template.createdAt)}</TableCell>
                  <TableCell align='right'>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                      }}
                    >
                      <Tooltip title='View'>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenView(template)}
                          color='primary'
                        >
                          <Visibility fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Edit'>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenEdit(template)}
                          color='primary'
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={deleteTooltip || 'Delete'}>
                        <span>
                          <IconButton
                            size='small'
                            disabled={
                              !canDelete || deleteTemplateMutation.isLoading
                            }
                            color='error'
                            onClick={() => handleDeleteClick(template.id)}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const FIELD_LABELS: Record<string, string> = {
    name: 'Name',
    createdAt: 'Created At',
    isDefault: 'Use by default',
    description: 'Description',
    temperature: 'Temperature',
    systemPrompt: 'Main prompt',
  };

  const DEFAULT_VALUES_TOOLTIP = 'These values are used by default';
  const TEMPERATURE_TOOLTIP = `Range: 0.0 – 2.0
• 0.2 = strict, almost identical responses every time
• 0.7 = diverse, but within the rules (recommended)
• 1.5+ = creative, may "fantasize"`;

  const changedFields = getChangedFields(originalTemplate, editTemplate);

  return (
    <MainLayout>
      <Stack
        spacing={2}
        width='100%'
      >
        <Typography
          variant='h4'
          gutterBottom
        >
          Settings
        </Typography>

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '& .MuiAccordionSummary-content': {
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 2,
              },
            }}
          >
            <Typography variant='h6'>LLM Templates</Typography>
            <Button
              variant='contained'
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCreate();
              }}
            >
              Create
            </Button>
          </AccordionSummary>
          <AccordionDetails>{renderTemplatesContent()}</AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>Initial Prompt</Typography>
          </AccordionSummary>
          <AccordionDetails>{renderInitialPromptContent()}</AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>Voices</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer
              component={Paper}
              variant='outlined'
            >
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell></TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>Category</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>Name</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>Age</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>
                        Expressed Gender
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>Language</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>Accent</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='subtitle2'>
                        Characteristics
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {VOICES.map((voice) => (
                    <TableRow
                      key={voice.name}
                      hover
                    >
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() => handleToggleVoice(voice.model)}
                        >
                          {playingModel === voice.model ? (
                            <Pause fontSize='small' />
                          ) : (
                            <PlayArrow fontSize='small' />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{voice.category}</TableCell>
                      <TableCell>
                        {voice.name}{' '}
                        {voice.featured ? (
                          <StarIcon
                            color='success'
                            fontSize='small'
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {voice.age.map((a, i) => (
                          <div key={i}>{a}</div>
                        ))}
                      </TableCell>
                      <TableCell>{voice.gender}</TableCell>
                      <TableCell>{voice.language}</TableCell>
                      <TableCell>{voice.accent}</TableCell>
                      <TableCell>
                        {voice.characteristics.map((c, i) => (
                          <div key={i}>{c}</div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* View Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseView}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>View Template</DialogTitle>
        <DialogContent>
          {viewTemplate && (
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
              }}
            >
              {Object.entries(FIELD_LABELS).map(([key, label]) => {
                const value = (
                  viewTemplate as unknown as Record<string, unknown>
                )[key];
                let displayValue: React.ReactNode;

                if (key === 'createdAt') {
                  displayValue = formatDate(value as string);
                } else if (key === 'isDefault') {
                  displayValue = value ? 'Yes' : 'No';
                } else if (
                  value === undefined ||
                  value === null ||
                  value === ''
                ) {
                  displayValue = <span style={{ color: '#888' }}>(empty)</span>;
                } else {
                  displayValue = String(value);
                }

                const hasDefaultTooltip = [
                  'goodbye',
                  'greeting',
                  'introduction',
                ].includes(key);
                const hasTemperatureTooltip = key === 'temperature';

                const labelContent = (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: 'primary.main',
                      }}
                    >
                      {label}:
                    </Typography>
                    {hasDefaultTooltip && (
                      <Tooltip
                        title={DEFAULT_VALUES_TOOLTIP}
                        arrow
                        placement='top'
                      >
                        <HelpOutline
                          fontSize='small'
                          color='action'
                          sx={{ cursor: 'help' }}
                        />
                      </Tooltip>
                    )}
                    {hasTemperatureTooltip && (
                      <Tooltip
                        title={TEMPERATURE_TOOLTIP}
                        arrow
                        placement='top'
                      >
                        <HelpOutline
                          fontSize='small'
                          color='action'
                          sx={{ cursor: 'help' }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                );

                return (
                  <Box
                    key={key}
                    sx={{ mb: 2 }}
                  >
                    {labelContent}
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        pl: 1,
                      }}
                    >
                      {displayValue}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseView}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEdit}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {isCreateMode ? 'Create Template' : 'Edit Template'}
        </DialogTitle>
        <DialogContent>
          {editTemplate && (
            <Stack
              spacing={2}
              sx={{ mt: 1 }}
            >
              <TextField
                fullWidth
                required
                label='Name'
                value={editTemplate.name}
                onChange={(e) => handleEditChange('name', e.target.value)}
                variant='outlined'
                size='small'
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={editTemplate.isDefault}
                    onChange={(e) =>
                      handleEditChange('isDefault', e.target.checked)
                    }
                    size='small'
                  />
                }
                label='Is Default'
              />

              <TextField
                fullWidth
                label='Description'
                value={editTemplate.description || ''}
                onChange={(e) =>
                  handleEditChange('description', e.target.value)
                }
                variant='outlined'
                size='small'
                multiline
                rows={3}
                sx={{ '& .MuiInputBase-root textarea': { resize: 'both' } }}
              />

              <TextField
                fullWidth
                required
                label='Temperature'
                type='number'
                inputProps={{ step: 0.1, min: 0, max: 2 }}
                value={editTemplate.temperature ?? ''}
                onChange={(e) =>
                  handleEditChange(
                    'temperature',
                    e.target.value === ''
                      ? undefined
                      : parseFloat(e.target.value)
                  )
                }
                variant='outlined'
                size='small'
              />

              <TextField
                fullWidth
                required
                label='System Prompt'
                value={editTemplate.systemPrompt || ''}
                onChange={(e) =>
                  handleEditChange('systemPrompt', e.target.value)
                }
                variant='outlined'
                size='small'
                multiline
                rows={4}
                sx={{ '& .MuiInputBase-root textarea': { resize: 'both' } }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEdit}
            disabled={
              updateTemplateMutation.isLoading ||
              createTemplateMutation.isLoading
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveClick}
            variant='contained'
            disabled={
              isCreateMode
                ? !isFormValid || createTemplateMutation.isLoading
                : !isFormValid ||
                  updateTemplateMutation.isLoading ||
                  changedFields.length === 0
            }
          >
            {isCreateMode ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Update Modal */}
      <ConfirmModal
        open={confirmUpdateOpen}
        title='Confirm Update'
        onCancel={() => setConfirmUpdateOpen(false)}
        onConfirm={handleConfirmUpdate}
        isLoading={updateTemplateMutation.isLoading}
        confirmText='Update'
      >
        <Stack spacing={1}>
          <Typography
            variant='body1'
            gutterBottom
          >
            Are you sure you want to update this template? The following changes
            will be applied:
          </Typography>
          {changedFields.map((change) => (
            <Paper
              key={change.field}
              variant='outlined'
              sx={{ p: 1.5, backgroundColor: 'grey.50' }}
            >
              <Typography
                variant='subtitle2'
                color='primary'
                gutterBottom
              >
                {change.field}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  <strong>Old:</strong> {change.oldValue}
                </Typography>
                <Typography variant='body2'>
                  <strong>New:</strong> {change.newValue}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </ConfirmModal>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={confirmDeleteOpen}
        title='Delete Template'
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setSelectedTemplateId(null);
        }}
        onConfirm={handleConfirmDelete}
        isLoading={deleteTemplateMutation.isLoading}
        confirmText='Delete'
        confirmColor='error'
      >
        Are you sure you want to delete this template? This action cannot be
        undone.
      </ConfirmModal>
    </MainLayout>
  );
};
